<script lang="ts">
export default {
  name: 'YearFilter',
};
</script>

<script setup lang="ts">
import FilterButtonV2 from '@/components/v2/FilterButton.vue';
import AppButton from '@/components/v2/AppButton.vue';
import { ref, computed, watch } from 'vue';

const COMPARE_TOOLTIP = "Select two or more years to compare how carrier's rates have changed over time";

const props = defineProps<{
  years: number[];
  modelValue: number[];
}>();

const emit = defineEmits<{
  (e: 'input', value: number[]): void;
  (e: 'update:modelValue', value: number[]): void;
  (e: 'compare'): void;
  (e: 'aggregate'): void;
}>();

const selected = ref<number[]>([]);

const title = computed(() => {
  if (selected.value.length === 1) {
    return selected.value[0].toString();
  }
  return 'Year';
});

function clear() {
  selected.value = [];
}

function aggregate() {
  emit('update:modelValue', selected.value);
  emit('aggregate');
}

function compare() {
  emit('update:modelValue', selected.value);
  emit('compare');
}

function selectAll() {
  selected.value = props.years;
}

watch(
  () => props.modelValue,
  (newValue) => {
    selected.value = newValue;
  },
  { immediate: true },
);
</script>

<template>
  <FilterButtonV2
    :title="title"
    :active="selected.length > 0"
    start
    data-test-activator="year-filter">
    <template #menu="{ close }">
      <div class="py-4 pt-0">
        <h4 class="mx-4 mt-4 mb-1">Years</h4>
        <div class="mx-4 mb-4 flex flex-col pl-2">
          <label
            v-for="year in props.years"
            :key="year"
            class="cursor-pointer items-center py-1 select-none">
            <input
              type="checkbox"
              v-model="selected"
              :value="year"
              data-testid="filter-item-carrier-group-checkbox" />
            <span class="mx-2">{{ year }}</span>
          </label>
        </div>
        <div class="flex justify-between gap-1 px-4">
          <AppButton
            outline
            @click="clear()"
            data-testid="filter-carrier-group-clear"
            >Clear</AppButton
          >
          <AppButton
            secondary
            @click="selectAll"
            data-testid="filter-carrier-group-select-all"
            >Select All</AppButton
          >
          <AppButton
            primary
            @click="
              aggregate();
              close();
            "
            data-testid="filter-carrier-group-apply"
            >Aggregate</AppButton
          >
          <div
            class="tooltip tooltip-top flex items-center"
            :data-tip="COMPARE_TOOLTIP">
            <AppButton
              primary
              :disabled="selected.length < 2"
              @click="
                compare();
                close();
              "
              data-testid="filter-carrier-group-compare"
              >Compare</AppButton
            >
          </div>
        </div>
      </div>
    </template>
  </FilterButtonV2>
</template>
